/**
 * Field-tagged validation errors from submit and command endpoints.
 *
 * The backend returns one FieldError per offending input on HTTP 400
 * with the shape `{ detail: { message, errors: FieldError[] } }`. See
 * `modules/field_errors.py` and
 * `.claude/rules/api-validation.md` for the convention.
 *
 * Frontends use `extractFieldErrors` to read them off an ApiError, then
 * pass them to `applyFieldErrors` along with their form library's
 * `setError` (works with react-hook-form, Formik, or a plain state map).
 */

import { ApiError } from "./api/errors";

export type FieldError = {
  field: string;
  code: string;
  message: string;
};

type ValidationFailedBody = {
  detail: {
    message: string;
    errors: FieldError[];
  };
};

function isFieldError(value: unknown): value is FieldError {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.field === "string" &&
    typeof v.code === "string" &&
    typeof v.message === "string"
  );
}

function isValidationFailedBody(value: unknown): value is ValidationFailedBody {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  const detail = v.detail as Record<string, unknown> | undefined;
  if (!detail || typeof detail !== "object") return false;
  if (typeof detail.message !== "string") return false;
  if (!Array.isArray(detail.errors)) return false;
  return detail.errors.every(isFieldError);
}

/** "payment_method" as a person reads it: the last name in the path, in words. */
function missingFieldName(loc: readonly (string | number)[]): string {
  const last = [...loc].reverse().find((part) => typeof part === "string");
  const words = String(last ?? "field").replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Read a list of FieldError off an unknown caught value, returning `[]`
 * when the value is not a field-tagged validation failure. Safe to call
 * on any catch parameter.
 */
export function extractFieldErrors(err: unknown): FieldError[] {
  if (!(err instanceof ApiError)) return [];
  // FastAPI/Pydantic rejects malformed types before the action can return
  // canonical errors. Normalise body-field paths to the same form contract.
  if (err.status === 422) {
    const detail = (err.body as { detail?: unknown } | null)?.detail;
    if (!Array.isArray(detail)) return [];
    return detail.flatMap((error: unknown) => {
      if (!error || typeof error !== "object") return [];
      const { loc, msg, type } = error as Record<string, unknown>;
      if (
        !Array.isArray(loc) ||
        loc[0] !== "body" ||
        loc.length < 2 ||
        !loc.every(
          (part) => typeof part === "string" || typeof part === "number",
        ) ||
        typeof msg !== "string" ||
        typeof type !== "string"
      )
        return [];
      const field = loc.slice(1).join(".");
      // Pydantic's bare "Field required" names nothing: say which field.
      return [
        {
          field,
          code: type,
          message:
            type === "missing" ? `${missingFieldName(loc)} is required.` : msg,
        },
      ];
    });
  }
  if (err.status !== 400) return [];
  if (!isValidationFailedBody(err.body)) return [];
  return err.body.detail.errors;
}

/**
 * Read Tharstern's upstream-rejection envelope returned on 502 Bad Gateway
 * from submit endpoints. Tharstern's response body looks like
 * `{ message, error_class, reasons: string[] }` — pages should show the
 * reasons array as bullet points rather than the raw JSON envelope.
 * Returns `[]` if the error isn't a Tharstern bad-gateway with reasons.
 */
export function extractTharsternReasons(err: unknown): string[] {
  if (!(err instanceof ApiError)) return [];
  if (err.status !== 502) return [];
  // FastAPI wraps HTTPException(detail=...) in `{"detail": ...}`, so the
  // reasons array lives at body.detail.reasons. See
  // modules/tharstern_estimating_module/api/routes/estimating.py.
  const body = err.body as Record<string, unknown> | null;
  if (!body || typeof body !== "object") return [];
  const detail = body.detail as Record<string, unknown> | undefined;
  if (!detail || typeof detail !== "object") return [];
  const reasons = detail.reasons;
  if (!Array.isArray(reasons)) return [];
  return reasons.filter((r): r is string => typeof r === "string");
}

/**
 * Detect whether a caught error is a Tharstern business_rule_violation —
 * i.e. Tharstern's pricing/configuration rules rejected the request. Used
 * by the cost-centres editor to show a short "Not available for this
 * product type" message instead of dumping the empty-reasons 502
 * envelope.
 */
export function isTharsternBusinessRuleViolation(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status !== 502) return false;
  const body = err.body as Record<string, unknown> | null;
  if (!body || typeof body !== "object") return false;
  const detail = body.detail as Record<string, unknown> | undefined;
  if (!detail || typeof detail !== "object") return false;
  return detail.error_class === "business_rule_violation";
}

/**
 * Read the plain-string `detail.errors` list off a caught error — the
 * `{ detail: { message, errors: string[] } }` envelope the catalogue's
 * narrowing routes return on 422, one line per refusal. Returns `[]` when the
 * error isn't shaped this way.
 */
export function extractErrorDetailLines(err: unknown): string[] {
  if (!(err instanceof ApiError)) return [];
  const body = err.body as Record<string, unknown> | null;
  const detail = body?.detail as Record<string, unknown> | undefined;
  if (!detail || typeof detail !== "object" || !Array.isArray(detail.errors))
    return [];
  return detail.errors.filter((e): e is string => typeof e === "string");
}

/**
 * Read the `detail.message` string off a caught error, for endpoints that
 * use the plain `{ detail: { message } }` envelope rather than the
 * field-tagged validation shape above — e.g. shopfloor_kiosk_module's
 * shift/job-control routes, where a 409 (no shift pattern assigned, no
 * active operation) and a 502 (Tharstern rejected the call) both carry a
 * human-readable `message` and nothing else. Returns `null` when the
 * error isn't shaped this way, so callers can fall back to a generic
 * message rather than a raw JSON dump.
 */
export function extractErrorDetailMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as Record<string, unknown> | null;
  if (!body || typeof body !== "object") return null;
  const detail = body.detail as Record<string, unknown> | undefined;
  if (!detail || typeof detail !== "object") return null;
  return typeof detail.message === "string" ? detail.message : null;
}

/**
 * A permanent refusal from a Tharstern submit route: the request will fail
 * the same way until somebody changes something, so retrying is useless.
 */
export type RefusalDetail = {
  message: string;
  reasonCode: string;
  remedy: string;
};

/**
 * Read the permanent-refusal envelope returned on 409 by the catalogue
 * send-estimate / place-order routes: `{ detail: { message, reason_code,
 * remedy } }`. Not the FieldError shape — nothing the CSR typed is wrong,
 * so there is no input to highlight.
 *
 * `remedy` is the half worth surfacing: it names who can actually fix it.
 * Showing only `message` leaves the CSR knowing it failed but not what to
 * do, which is how "user not linked to Tharstern" reads as a generic fault.
 * See catalogue_module's `_refusal_detail`.
 */
export function extractRefusalDetail(err: unknown): RefusalDetail | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status !== 409) return null;
  const body = err.body as Record<string, unknown> | null;
  if (!body || typeof body !== "object") return null;
  const detail = body.detail as Record<string, unknown> | undefined;
  if (!detail || typeof detail !== "object") return null;
  const { message, reason_code: reasonCode, remedy } = detail;
  if (
    typeof message !== "string" ||
    typeof reasonCode !== "string" ||
    typeof remedy !== "string"
  ) {
    return null;
  }
  return { message, reasonCode, remedy };
}

/**
 * One contact who has already been touched by `recentCampaignCount`
 * distinct campaigns (excluding call-channel touches) within the
 * frequency-cap window.
 */
export type ActivationCapWarning = {
  personId: string;
  personName: string;
  recentCampaignCount: number;
};

export type ActivationCapExceeded = {
  message: string;
  threshold: number;
  windowDays: number;
  warnings: ActivationCapWarning[];
};

/**
 * Read the campaign-frequency-cap envelope returned on 409 by
 * `POST /marketing/campaigns/{id}/activate`: `{ detail: { message,
 * threshold, window_days, warnings } }`. Returns null when the error isn't
 * shaped this way (e.g. it's the unrelated 409 `RefusalDetail` shape used by
 * catalogue submit routes). See ActivationCapExceededResponse in
 * modules/marketing_campaigns_module/api/schemas/campaigns.py. Re-call
 * activation with `{ confirmOverCap: true }` to send anyway.
 */
export function extractActivationCapExceeded(
  err: unknown,
): ActivationCapExceeded | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status !== 409) return null;
  const body = err.body as Record<string, unknown> | null;
  const detail = body?.detail as Record<string, unknown> | undefined;
  if (!detail || !Array.isArray(detail.warnings)) return null;
  return {
    message: typeof detail.message === "string" ? detail.message : "",
    threshold: typeof detail.threshold === "number" ? detail.threshold : 0,
    windowDays: typeof detail.window_days === "number" ? detail.window_days : 0,
    warnings: (detail.warnings as Record<string, unknown>[]).map((w) => ({
      personId: typeof w.person_id === "string" ? w.person_id : "",
      personName: typeof w.person_name === "string" ? w.person_name : "",
      recentCampaignCount:
        typeof w.recent_campaign_count === "number"
          ? w.recent_campaign_count
          : 0,
    })),
  };
}

/**
 * Apply field errors to a form via its library's `setError(field, ...)`.
 * Library-agnostic — the callback signature matches react-hook-form,
 * Formik, or any custom equivalent.
 */
export function applyFieldErrors(
  setError: (field: string, error: { message: string }) => void,
  errors: FieldError[],
): void {
  for (const err of errors) {
    setError(err.field, { message: err.message });
  }
}
